import {
  Box,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import BookingReferenceItem from 'components/Bookings/BookingReferenceItem/BookingReferenceItem'
import SectionHeader from 'components/Common/SectionHeader'
import ConnectForm from 'components/RouteWizard/ConnectForm'
import Counter from 'components/RouteWizard/Counter'
import useBookingReferences from 'hooks/useBookingReferences'
import React, { useEffect } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Passenger } from '../AccountInformation/AdditionalPassengerDetails'
import MainPassengerDetails from '../AccountInformation/MainPassengerDetails'
import Passengers from '../AccountInformation/Passengers'

const DefaultSourceToggleGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  marginTop: '0.5rem',
  marginBottom: '0.75rem',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.5rem',
  '& .MuiToggleButton-root': {
    margin: 0,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    fontWeight: 500,
    padding: '0.375rem 0.875rem',
    borderRadius: 8,
    textTransform: 'none',
    textWrap: 'nowrap',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.background.default,
      borderColor: theme.palette.secondary.main,
    },
    '&.Mui-selected': {
      backgroundColor: theme.palette.brand.dark,
      color: theme.palette.common.white,
      borderColor: theme.palette.brand.dark,
      '&:hover': {
        backgroundColor: theme.palette.brand.light,
        borderColor: theme.palette.brand.light,
      },
    },
  },
}))

interface Props {
  onlyTraveller: boolean
  clientId: number
  selectedClientId: string
  numPassengers: number
  maxPassenger?: number
  setNumPassengers: React.Dispatch<React.SetStateAction<number>>
}

const AccountReference = ({
  clientId,
  selectedClientId,
  numPassengers,
  maxPassenger,
  setNumPassengers,
}: Props) => {
  const { register, watch, control } = useFormContext()

  // watch the switch value directly from RHF
  const useLeadPassengerDefaults = watch(
    'bookingReferences.UseLeadPassengerDefaults',
    false,
  )
  const { bookingReferences, isLoading } = useBookingReferences(clientId)

  const [leadPassenger, setLeadPassenger] = React.useState<Passenger>()

  const onPassengerChange = (passenger: Passenger) => {
    setLeadPassenger(passenger)
  }

  // still need to register this field once
  useEffect(() => {
    register('bookingReferences.UseLeadPassengerDefaults')
  }, [register])

  return (
    <>
      {selectedClientId !== '0' && (
        <>
          <SectionHeader
            title="who will be travelling"
            subtitle="Select lead and additional passengers."
          />
          <ConnectForm>
            <Box
              component="div"
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '10px',
              }}
            >
              <MainPassengerDetails
                clientId={clientId}
                onPassengerChange={onPassengerChange}
              />

              <Counter
                value={numPassengers}
                label="passenger"
                negativeOnClick={setNumPassengers}
                negativeDisabled={numPassengers === 1}
                positiveOnClick={(num) => {
                  if (maxPassenger === undefined || num <= maxPassenger) {
                    setNumPassengers(num)
                  }
                  window.scrollTo(0, document.body.scrollHeight)
                }}
                positiveDisabled={
                  maxPassenger !== undefined && numPassengers >= maxPassenger
                }
              />
            </Box>
          </ConnectForm>
          {numPassengers > 1 && (
            <>
              <SectionHeader
                title="additional passengers"
                subtitle="Additional passenger details."
              />
              <Passengers
                numPassengers={numPassengers - 1}
                setNumPassengers={setNumPassengers}
                clientId={clientId}
              />
            </>
          )}
        </>
      )}

      {(isLoading || bookingReferences?.length > 0) && (
        <div>
          <SectionHeader
            title="Account References"
            subtitle="Please complete your company account references:"
            containerStyles={{ marginBottom: '0' }}
          />
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}
            >
              Default reference values from
            </Typography>
            <Controller
              name="bookingReferences.UseLeadPassengerDefaults"
              control={control}
              defaultValue={false}
              render={({ field: { value, onChange } }) => (
                <DefaultSourceToggleGroup
                  exclusive
                  value={value ? 'lead' : 'booker'}
                  onChange={(_, next) => {
                    if (next !== null) onChange(next === 'lead')
                  }}
                  aria-label="Default reference values source"
                >
                  <ToggleButton value="booker">Booker</ToggleButton>
                  <ToggleButton value="lead">Lead passenger</ToggleButton>
                </DefaultSourceToggleGroup>
              )}
            />
          </Box>
        </div>
      )}

      {isLoading && <CircularProgress size={12} />}

      {!isLoading &&
        bookingReferences?.map((bookingRef: any, index: number) => (
          <BookingReferenceItem
            key={`${bookingRef.title}-${index}`}
            index={index}
            item={bookingRef}
            leadPassenger={leadPassenger}
            useLeadPassengerDefaults={useLeadPassengerDefaults}
          />
        ))}

      {(isLoading || bookingReferences?.length > 0) && (
        <Box
          component="hr"
          sx={{
            margin: '20px 0',
            borderColor: '#828282',
            opacity: '0.2',
          }}
        />
      )}
    </>
  )
}

export default AccountReference
